import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BookOpen, Download, RefreshCw, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice as fmtINR } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_TYPES = ['Customer Payment', 'Vendor Payment', 'Expense', 'Capital Investment', 'Capital Drawing'];
const TYPE_COLORS = {
  'Customer Payment': 'bg-lime-100 text-lime-800 border-lime-200',
  'Vendor Payment': 'bg-red-100 text-red-800 border-red-200',
  'Expense': 'bg-amber-100 text-amber-800 border-amber-200',
  'Capital Investment': 'bg-blue-100 text-blue-800 border-blue-200',
  'Capital Drawing': 'bg-violet-100 text-violet-800 border-violet-200',
};

export default function Ledger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [methodFilter, setMethodFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await axios.get(`${BACKEND_URL}/api/ledger${params.toString() ? `?${params}` : ''}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLedger(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  // Local filters (type, method, search)
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.entries.filter(e => {
      if (typeFilter !== 'ALL' && e.type !== typeFilter) return false;
      if (methodFilter !== 'ALL' && e.method !== methodFilter) return false;
      if (q) {
        const hay = `${e.account} ${e.description} ${e.reference}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, typeFilter, methodFilter, search]);

  const totals = useMemo(() => {
    const t = { in: 0, out: 0 };
    for (const e of filtered) { t.in += e.in; t.out += e.out; }
    return { in: t.in, out: t.out, net: t.in - t.out };
  }, [filtered]);

  const allMethods = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.entries.map(e => e.method).filter(Boolean))).sort();
  }, [data]);

  // Group entries by date for day-wise display
  const dayGroups = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const d = e.date || 'No date';
      if (!map.has(d)) map.set(d, { entries: [], in: 0, out: 0 });
      const g = map.get(d);
      g.entries.push(e);
      g.in += e.in;
      g.out += e.out;
    }
    return Array.from(map.entries()).map(([date, g]) => ({ date, ...g, net: g.in - g.out }));
  }, [filtered]);

  const exportCsv = () => {
    if (!filtered.length) { toast.error('No entries to export'); return; }
    const header = ['Date', 'Type', 'Account', 'Description', 'In (₹)', 'Out (₹)', 'Method', 'Reference', 'Running Balance (₹)'];
    const rows = filtered.map(e => [
      e.date, e.type, e.account, (e.description || '').replace(/\n/g, ' '),
      e.in || '', e.out || '', e.method, e.reference, e.running_balance,
    ]);
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nirmaan-ledger-${(startDate || 'all')}_to_${(endDate || 'today')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setStartDate(''); setEndDate(''); setTypeFilter('ALL'); setMethodFilter('ALL'); setSearch('');
  };

  if (loading && !data) return <div className="text-stone-600">Loading ledger...</div>;
  if (!data) return <div className="text-red-600">Failed to load ledger.</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <BookOpen className="w-6 h-6 text-amber-700" /> Day-wise Ledger
          </h1>
          <p className="text-xs text-stone-500 mt-1">Unified journal of every cash movement — customer payments, vendor payments, expenses, partner capital</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="ledger-refresh" variant="outline" size="sm" onClick={fetchLedger} className="text-xs h-8 border-stone-200">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button data-testid="ledger-export" variant="outline" size="sm" onClick={exportCsv} className="text-xs h-8 border-amber-200 text-amber-800 hover:bg-amber-50">
            <Download className="w-3 h-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Totals bar */}
      <div data-testid="ledger-totals" className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-lime-200 bg-lime-50/60 p-3">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-lime-700">Total Inflow</div>
          <div className="text-xl font-bold tabular-nums text-lime-800 mt-1">&#8377;{fmtINR(totals.in)}</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-red-700">Total Outflow</div>
          <div className="text-xl font-bold tabular-nums text-red-800 mt-1">&#8377;{fmtINR(totals.out)}</div>
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-stone-500">Net Cash Movement</div>
          <div className={`text-xl font-bold tabular-nums mt-1 ${totals.net >= 0 ? 'text-lime-800' : 'text-red-800'}`}>{totals.net >= 0 ? '+' : '−'}&#8377;{fmtINR(Math.abs(totals.net))}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-stone-200 bg-white p-3 flex items-end gap-3 flex-wrap">
        <div className="flex-shrink-0">
          <Label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">From</Label>
          <Input data-testid="ledger-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border-stone-200 h-8 text-xs w-36" />
        </div>
        <div className="flex-shrink-0">
          <Label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">To</Label>
          <Input data-testid="ledger-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border-stone-200 h-8 text-xs w-36" />
        </div>
        <div className="flex-shrink-0">
          <Label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="ledger-type-filter" className="border-stone-200 h-8 text-xs w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All types</SelectItem>
              {ALL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {allMethods.length > 0 && (
          <div className="flex-shrink-0">
            <Label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Method</Label>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger data-testid="ledger-method-filter" className="border-stone-200 h-8 text-xs w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All methods</SelectItem>
                {allMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Search</Label>
          <Input data-testid="ledger-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Account, description, reference…" className="border-stone-200 h-8 text-xs" />
        </div>
        {(startDate || endDate || typeFilter !== 'ALL' || methodFilter !== 'ALL' || search) && (
          <Button variant="outline" size="sm" onClick={clearFilters} className="h-8 text-[10px] border-stone-200">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Day-grouped entries */}
      <div className="space-y-3">
        {dayGroups.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white p-8 text-center text-xs text-stone-500">No movements in this period.</div>
        ) : dayGroups.map(g => (
          <div key={g.date} className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-stone-50 border-b border-stone-200 text-[11px]">
              <span className="font-bold text-stone-800 tabular-nums">{g.date}</span>
              <div className="flex gap-4 text-[10px]">
                <span className="text-lime-700">In: <strong className="tabular-nums">&#8377;{fmtINR(g.in)}</strong></span>
                <span className="text-red-700">Out: <strong className="tabular-nums">&#8377;{fmtINR(g.out)}</strong></span>
                <span className={g.net >= 0 ? 'text-lime-800' : 'text-red-800'}>
                  Net: <strong className="tabular-nums">{g.net >= 0 ? '+' : '−'}&#8377;{fmtINR(Math.abs(g.net))}</strong>
                </span>
              </div>
            </div>
            <table className="w-full text-[11px]">
              <thead className="bg-stone-50/70 border-b border-stone-100">
                <tr>
                  <th className="text-left p-2 font-semibold text-stone-500 uppercase tracking-wide">Type</th>
                  <th className="text-left p-2 font-semibold text-stone-500 uppercase tracking-wide">Account</th>
                  <th className="text-left p-2 font-semibold text-stone-500 uppercase tracking-wide">Description</th>
                  <th className="text-right p-2 font-semibold text-stone-500 uppercase tracking-wide">In</th>
                  <th className="text-right p-2 font-semibold text-stone-500 uppercase tracking-wide">Out</th>
                  <th className="text-left p-2 font-semibold text-stone-500 uppercase tracking-wide">Method</th>
                  <th className="text-right p-2 font-semibold text-stone-500 uppercase tracking-wide">Running</th>
                </tr>
              </thead>
              <tbody>
                {g.entries.map(e => (
                  <tr key={`${e.source}-${e.id}`} data-testid={`ledger-row-${e.id}`} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50/60">
                    <td className="p-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold border ${TYPE_COLORS[e.type] || 'bg-stone-100 text-stone-700 border-stone-200'}`}>{e.type}</span>
                    </td>
                    <td className="p-2 font-medium text-stone-800">{e.account || '—'}</td>
                    <td className="p-2 text-stone-600 max-w-md truncate" title={e.description}>{e.description || '—'}</td>
                    <td className="p-2 text-right tabular-nums text-lime-700 font-semibold">{e.in > 0 ? `${fmtINR(e.in)}` : '—'}</td>
                    <td className="p-2 text-right tabular-nums text-red-700 font-semibold">{e.out > 0 ? `${fmtINR(e.out)}` : '—'}</td>
                    <td className="p-2 text-stone-600">{e.method || '—'}</td>
                    <td className={`p-2 text-right tabular-nums font-mono font-semibold ${e.running_balance >= 0 ? 'text-stone-700' : 'text-red-700'}`}>&#8377;{fmtINR(e.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
